import { Box, Chip, CircularProgress, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useDriverConversations } from 'src/hooks/useDriverConversations';
import { TranscriptPanel } from '../transcript/TranscriptPanel';
import { RecordingSidebar } from '../transcript/RecordingSidebar';
import { TabLabel } from '../transcript/TabLabel';

export const LeadAiRecruiterLog = ({ currentDriver, companyId, userToken }) => {
  const conversationIds = useMemo(() => {
    const conversationsArray = Array.isArray(currentDriver?.lead_conversation_ids)
      ? currentDriver.lead_conversation_ids
      : currentDriver?.lead_conversation_id
      ? [currentDriver.lead_conversation_id]
      : [];
    return conversationsArray.filter(Boolean);
  }, [currentDriver]);

  const { ids, transcripts, audio, loadingTranscripts, loadAudio } = useDriverConversations({
    companyId,
    conversationIds,
  });
console.log(transcripts)
  const isBooting = useMemo(() => ids.length > 0 && Object.keys(transcripts || {}).length === 0, [ids, transcripts]);
  const [directionFilter, setDirectionFilter] = useState('all');

  const grouped = useMemo(() => {
    const enriched = ids.map((id) => ({
      id,
      t: transcripts[id] || {},
      dir: (transcripts[id]?.transcriptDirection || '').toLowerCase(),
      start: Number(transcripts[id]?.transcriptStartTime) || 0,
    }));
    const sortDesc = (a, b) => b.start - a.start;

    const inbound = enriched.filter((x) => x.dir === 'inbound').sort(sortDesc);
    const outbound = enriched.filter((x) => x.dir === 'outbound').sort(sortDesc);
    const all = [...inbound, ...outbound].sort(sortDesc);

    return { all, inbound, outbound };
  }, [ids, transcripts]);

  const activeList =
    directionFilter === 'inbound' ? grouped.inbound : directionFilter === 'outbound' ? grouped.outbound : grouped.all;

  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    setActiveId(ids[0] ?? null);
  }, [ids]);

  useEffect(() => {
    if (activeList.length === 0) {
      setActiveId(null);
      return;
    }
    if (!activeId || !activeList.some((x) => x.id === activeId)) {
      setActiveId(activeList[0].id);
    }
  }, [directionFilter, grouped, activeList, activeId]);

  useEffect(() => {
    if (!activeId) return;
    if (!transcripts[activeId]) return;
    loadAudio(activeId);
  }, [activeId, transcripts, loadAudio]);

  useEffect(() => {
    return () => {
      const url = activeId ? audio[activeId]?.url : null;
      if (url) URL.revokeObjectURL(url);
    };
  }, [activeId, audio]);

  return (
    <Box textAlign="center" p={1} borderradius="12px" boxShadow={3} sx={{ mb: '20px', minHeight: '62vh' }}>
      {/* Loader has priority only until we have at least 1 transcript */}
      {isBooting || loadingTranscripts ? (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: '62vh' }}>
          <CircularProgress size={64} />
        </Box>
      ) : conversationIds.length === 0 ? (
        <Typography fontSize="20px" fontWeight="800">
          No record found
        </Typography>
      ) : (
        <>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <ToggleButtonGroup
              size="small"
              value={directionFilter}
              exclusive
              onChange={(_, v) => v && setDirectionFilter(v)}
            >
              <ToggleButton value="all">
                All <Chip size="small" sx={{ ml: 1 }} label={grouped.all.length} />
              </ToggleButton>
              <ToggleButton value="inbound">
                Inbound <Chip size="small" sx={{ ml: 1 }} label={grouped.inbound.length} />
              </ToggleButton>
              <ToggleButton value="outbound">
                Outbound <Chip size="small" sx={{ ml: 1 }} label={grouped.outbound.length} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {activeList.length > 0 && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
              <Tabs
                value={activeId ?? false}
                onChange={(_, v) => setActiveId(v)}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
              >
                {activeList.map((item) => (
                  <Tab
                    key={item.id}
                    value={item.id}
                    label={<TabLabel item={item} transcripts={transcripts} />}
                    sx={{ textTransform: 'none', minHeight: 44, pt: 0, borderradius: '5px' }}
                  />
                ))}
              </Tabs>
            </Box>
          )}

          <Box display="flex" justifyContent="space-between" width="100%" mt="8px" gap={2}>
            <Box flex={1} display="flex" flexDirection="column" alignItems="stretch">
              {activeList.length === 0 ? (
                <Typography fontSize="20px" fontWeight="800">
                  No calls in this group
                </Typography>
              ) : !activeId ? (
                <Typography>Select a call</Typography>
              ) : (
                <TranscriptPanel
                  conversation={transcripts[activeId]?.transcriptConversation ?? []}
                  status={transcripts[activeId]?.transcriptStatus}
                />
              )}
            </Box>

            <Box
              sx={{
                flexBasis: { xs: '100%', md: '38%' },
                flexShrink: 0,
                position: 'sticky',
                top: { md: 16 },
                alignSelf: 'flex-start',
                maxHeight: { md: 'calc(100vh - 32px)' },
                overflowY: { md: 'auto' },
                mx: 2,
                textAlign: 'left',
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 1.5,
                borderradius: 1,
              }}
            >
              {activeList.length === 0 || !activeId ? (
                <Typography color="text.secondary">Select a call to see details.</Typography>
              ) : (
                <RecordingSidebar
                  summary={transcripts[activeId]?.transcriptSummary}
                  startTime={transcripts[activeId]?.transcriptStartTime}
                  audioUrl={audio[activeId]?.url ?? null}
                  audioLoading={audio[activeId]?.loading ?? false}
                  audioError={audio[activeId]?.error ?? null}
                />
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
